"use client"

import * as React from "react"
import { PieChart, Pie, Cell, ResponsiveContainer, Area, AreaChart, CartesianGrid, XAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import type { ChartConfig } from "@/components/ui/chart"
import { Loader } from "@/components/ai-elements/loader"
import { TrendingUp, TrendingDown } from "@/components/icons"
import { RefreshCw, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type { InsightsData, RawGroupedItem } from "@/hooks/use-insights-v2"
import { useCalendars } from "@/context/calendars-context"

// Chart colors for grouping dimensions that need fixed colors
const PRIORITY_COLORS: Record<string, string> = {
  'High': 'var(--chart-4)',
  'Medium': 'var(--chart-3)',
  'Low': 'var(--chart-2)',
  'No priority': 'var(--chart-1)',
  'Unassigned': 'var(--secondary)',
}

const TYPE_COLORS: Record<string, string> = {
  'Time-block': 'var(--chart-3)',
  'Tasks-block': 'var(--chart-2)',
  'Habit': 'var(--chart-5)',
  'Unassigned': 'var(--secondary)',
}

type ViewByDimension = 'life_domain' | 'label' | 'priority' | 'type'

interface ChartItem {
  id: string
  name: string
  value: number
  percentage: number
  color: string
  isIntentional: boolean
}

/**
 * Groups raw data by a chosen dimension, filtering by selected calendars
 * and excluding toggled-off series. Recalculates percentages from visible series only.
 */
function groupRawData(
  rawData: RawGroupedItem[],
  groupBy: ViewByDimension,
  selectedCalendarIds: Set<string>,
  excludedSeries: Set<string>,
  periodDuration: number
): ChartItem[] {
  // Filter by selected calendars
  const filtered = rawData.filter(item => selectedCalendarIds.has(item.calendar_id))

  // Group by chosen dimension using unique IDs as keys
  const groups = new Map<string, { name: string; timeSpent: number; color: string; isIntentional: boolean; sortOrder: number }>()

  for (const item of filtered) {
    let id: string
    let name: string
    let color: string
    let sortOrder = 0

    switch (groupBy) {
      case 'life_domain':
        id = item.life_domain_id || item.life_domain_title
        name = item.life_domain_title
        color = item.life_domain_color
        sortOrder = item.life_domain_sort_order
        break
      case 'label':
        id = item.label_id || item.label_name
        name = item.label_name
        color = item.label_color
        sortOrder = 0
        break
      case 'priority':
        id = item.priority
        name = item.priority
        color = PRIORITY_COLORS[item.priority] || 'var(--secondary)'
        sortOrder = item.priority === 'High' ? 1 : item.priority === 'Medium' ? 2 : item.priority === 'Low' ? 3 : item.priority === 'No priority' ? 4 : 100
        break
      case 'type':
        id = item.events_type
        name = item.events_type
        color = TYPE_COLORS[item.events_type] || 'var(--secondary)'
        sortOrder = item.events_type === 'Time-block' ? 1 : item.events_type === 'Tasks-block' ? 2 : item.events_type === 'Habit' ? 3 : 100
        break
    }

    const existing = groups.get(id)
    if (existing) {
      existing.timeSpent += item.time_spent
      if (item.intentional_living) existing.isIntentional = true
    } else {
      groups.set(id, { name, timeSpent: item.time_spent, color, isIntentional: item.intentional_living, sortOrder })
    }
  }

  // Convert to sorted array
  const items = Array.from(groups.entries())
    .map(([id, data]) => ({ id, ...data }))
    .sort((a, b) => a.sortOrder - b.sortOrder)

  // Add unscheduled time
  const totalTimeSpent = items.reduce((sum, i) => sum + i.timeSpent, 0)
  const unscheduledTime = Math.max(0, periodDuration - totalTimeSpent)

  const allItems = [
    ...items.map(i => ({ ...i, timeSpent: Math.round(i.timeSpent * 10) / 10 })),
    { id: '__unscheduled__', name: 'Unscheduled', timeSpent: Math.round(unscheduledTime * 10) / 10, color: 'var(--background)', isIntentional: false, sortOrder: 1000 },
  ]

  // Calculate percentages based on visible series only (excluding toggled-off items)
  const visibleTotal = allItems
    .filter(i => !excludedSeries.has(i.id))
    .reduce((sum, i) => sum + i.timeSpent, 0)

  return allItems.map(item => ({
    id: item.id,
    name: item.name,
    value: item.timeSpent,
    percentage: !excludedSeries.has(item.id) && visibleTotal > 0
      ? Math.round((item.timeSpent / visibleTotal) * 100)
      : 0,
    color: item.color,
    isIntentional: item.isIntentional,
  }))
}

// Animated number display
function AnimatedNumber({ value, suffix = "%" }: { value: number; suffix?: string }) {
  const [displayValue, setDisplayValue] = React.useState(0)

  React.useEffect(() => {
    let frameId: number
    const startTime = Date.now()
    const duration = 1000

    function animate() {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const currentValue = Math.round(eased * value)
      setDisplayValue(currentValue)
      
      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
      }
    }

    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [value])

  return (
    <span className="text-5xl md:text-6xl font-bold tabular-nums text-primary-foreground">
      {displayValue}<span className="text-3xl md:text-4xl">{suffix}</span>
    </span>
  )
}

// Legend item with click-to-toggle
const LegendItem = ({ 
  name, 
  value, 
  percentage, 
  color, 
  isHovered,
  isExcluded,
  onMouseEnter, 
  onMouseLeave,
  onClick,
}: {
  name: string
  value: number
  percentage: number
  color: string
  isHovered: boolean
  isExcluded: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  onClick: () => void
}) => (
  <div 
    className={cn(
      "flex items-center gap-3 rounded-md transition-colors cursor-pointer",
      isHovered && "bg-muted",
      isExcluded && "opacity-40"
    )}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
  >
    <div 
      className="w-1 h-6 rounded-full"
      style={{ backgroundColor: color }}
    />
    <div className="flex items-center gap-2 flex-1">
      <span className={cn("text-sm font-medium", isExcluded && "line-through")}>{name}</span>
    </div>
    <div className="text-right">
      <div className="text-sm">
        <span className="font-medium">{value}h</span>{!isExcluded && <span className="text-muted-foreground"> ({percentage}%)</span>}
      </div>
    </div>
  </div>
)

// Doughnut chart with legend
const DoughnutChartContent = ({ 
  chartData, 
  chartConfig, 
  hoveredId,
  excludedSeries,
  onHover,
  onLeave,
  onToggleSeries,
}: {
  chartData: ChartItem[]
  chartConfig: ChartConfig
  hoveredId: string | null
  excludedSeries: Set<string>
  onHover: (id: string) => void
  onLeave: () => void
  onToggleSeries: (id: string) => void
}) => {
  const visibleData = chartData.filter(item => !excludedSeries.has(item.id))
  const hasData = visibleData.length > 0

  return (
    <div className="space-y-6">
      <div className="h-[300px] w-full">
        {hasData ? (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={visibleData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                  onMouseEnter={(_, index) => onHover(visibleData[index].id)}
                  onMouseLeave={onLeave}
                >
                  {visibleData.map((entry) => {
                    const isHovered = hoveredId === entry.id
                    return (
                      <Cell 
                        key={`cell-${entry.id}`} 
                        fill={entry.color}
                        stroke={isHovered ? entry.color : 'none'}
                        strokeWidth={isHovered ? 3 : 0}
                        style={{
                          filter: isHovered ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'none',
                          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                          transformOrigin: 'center',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      />
                    )
                  })}
                </Pie>
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      formatter={(value, name) => [
                        `${value}h (${visibleData.find(item => item.name === name)?.percentage}%)`,
                        name
                      ]}
                    />
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-muted-foreground text-sm">No data available</span>
          </div>
        )}
      </div>

      {chartData.length > 0 && (
        <div className="space-y-1 mt-6">
          {chartData.map((item) => (
            <LegendItem
              key={item.id}
              name={item.name}
              value={item.value}
              percentage={item.percentage}
              color={item.color}
              isHovered={hoveredId === item.id}
              isExcluded={excludedSeries.has(item.id)}
              onMouseEnter={() => onHover(item.id)}
              onMouseLeave={onLeave}
              onClick={() => onToggleSeries(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Calendar filter dropdown with checkboxes
function CalendarFilter({
  calendars,
  selectedIds,
  onToggle,
}: {
  calendars: Array<{ id: string; displayName: string }>
  selectedIds: Set<string>
  onToggle: (id: string) => void
}) {
  const selectedCount = selectedIds.size
  const totalCount = calendars.length

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          Calendars ({selectedCount}/{totalCount})
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-1">
          {calendars.map(cal => (
            <label
              key={cal.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer text-sm"
            >
              <Checkbox
                checked={selectedIds.has(cal.id)}
                onCheckedChange={() => onToggle(cal.id)}
              />
              <span className="truncate">{cal.displayName}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface IntentionalLivingScoreCardV2Props {
  data?: InsightsData | null
  intentionalLivingData?: InsightsData | null
  loading: boolean
  className?: string
  onRefresh?: () => void
  isRefreshing?: boolean
  /** When false, the refresh button inside the card is hidden (e.g. when refresh is in a separate top card). Default true. */
  showRefreshButton?: boolean
}

const intentionalLivingChartConfig: ChartConfig = {
  value: { label: "Intentional Living %" },
}

export function IntentionalLivingScoreCardV2({ data, intentionalLivingData, loading, className, onRefresh, isRefreshing, showRefreshButton = true }: IntentionalLivingScoreCardV2Props) {
  const { integrations } = useCalendars()

  // UI state
  const [hoveredId, setHoveredId] = React.useState<string | null>(null)
  const [viewBy, setViewBy] = React.useState<ViewByDimension>('life_domain')
  const [selectedCalendarIds, setSelectedCalendarIds] = React.useState<Set<string>>(new Set())
  const [excludedSeries, setExcludedSeries] = React.useState<Set<string>>(new Set())
  const [calendarIdsInitialized, setCalendarIdsInitialized] = React.useState(false)

  // Reset initialization flag when the set of available calendars changes
  const calendarIdsKey = data?.calendars?.map(c => c.id).sort().join(',') ?? ''
  React.useEffect(() => {
    setCalendarIdsInitialized(false)
  }, [calendarIdsKey])

  // Initialize selected calendars when data arrives
  React.useEffect(() => {
    if (data?.calendars && !calendarIdsInitialized) {
      setSelectedCalendarIds(new Set(data.calendars.map(c => c.id)))
      setCalendarIdsInitialized(true)
    }
  }, [data?.calendars, calendarIdsInitialized])

  // Reset excluded series when view changes
  React.useEffect(() => {
    setExcludedSeries(new Set())
  }, [viewBy])

  // Resolve calendar display names from CalendarsContext
  const calendarDisplayList = React.useMemo(() => {
    if (!data?.calendars) return []

    return data.calendars.map(cal => {
      let displayName = cal.name
      for (const integration of integrations) {
        for (const c of integration.calendars) {
          if (c.integrationCalendarId === cal.id) {
            displayName = c.summary
            break
          }
        }
      }
      return { id: cal.id, displayName }
    })
  }, [data?.calendars, integrations])

  // Compute chart data from raw grouped data
  const chartData = React.useMemo(() => {
    if (!data?.raw_data || !calendarIdsInitialized) return []
    const periodDuration = data?.aggregate?.period_duration ?? 0
    return groupRawData(
      data.raw_data,
      viewBy,
      selectedCalendarIds,
      excludedSeries,
      periodDuration
    )
  }, [data?.raw_data, data?.aggregate?.period_duration, viewBy, selectedCalendarIds, excludedSeries, calendarIdsInitialized])

  const chartConfig = React.useMemo(() => {
    return chartData.reduce((config, item, index) => {
      config[`item${index}`] = { label: item.name, color: item.color }
      return config
    }, {} as ChartConfig)
  }, [chartData])

  // Intentional living chart data
  const intentionalLivingChartData = intentionalLivingData?.daily?.map(item => ({
    day: item.day_label,
    value: item.intentional_living_percentage,
  })) || []

  const mainPercentage = intentionalLivingData?.aggregate?.intentional_living_percentage
  const hasIntentionalLivingData = intentionalLivingData && intentionalLivingData.aggregate && intentionalLivingData.daily && intentionalLivingData.daily.length > 0

  // Handlers
  const handleCalendarToggle = (id: string) => {
    setSelectedCalendarIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleToggleSeries = (id: string) => {
    setExcludedSeries(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (loading) {
    return (
      <Card className={cn("bg-primary pt-6 pb-2", className)}>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <CardTitle className="text-primary-foreground">Intentional living score</CardTitle>
          {onRefresh && showRefreshButton && (
            <Button 
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={isRefreshing || loading}
              className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground gap-1.5 px-2 md:px-3"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden md:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 space-y-3">
            <Loader size={24} className="text-primary-foreground" />
            <div className="text-primary-foreground/70 text-sm">Loading insights...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn("bg-primary pt-6 pb-2", className)}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-primary-foreground">Intentional living score</CardTitle>
          {intentionalLivingData?.updated_at && (
            <p className="text-xs text-primary-foreground/70">
              Last updated: {intentionalLivingData.updated_at}
            </p>
          )}
        </div>
        {onRefresh && showRefreshButton && (
          <Button 
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground gap-1.5 px-2 md:px-3"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </Button>
        )}
      </CardHeader>
      <CardContent data-slot="card-content" className="px-2">
        {/* Intentional Living Section */}
        <div className="flex items-center justify-between mb-6 px-4">
          <div className="flex flex-col gap-3">
            <div className="flex-shrink-0">
              {hasIntentionalLivingData ? (
                <AnimatedNumber value={mainPercentage || 0} />
              ) : (
                <span className="text-6xl font-bold tabular-nums text-primary-foreground">-</span>
              )}
            </div>
            {intentionalLivingData?.aggregate?.wow_intentional_living !== null && intentionalLivingData?.aggregate?.wow_intentional_living !== undefined && (
              <Badge 
                variant="secondary" 
                className={`w-fit bg-white ${
                  intentionalLivingData.aggregate.wow_intentional_living > 0 
                    ? 'text-green-600 hover:bg-green-50' 
                    : intentionalLivingData.aggregate.wow_intentional_living < 0 
                      ? 'text-red-600 hover:bg-red-50' 
                      : 'text-black hover:bg-gray-50'
                }`}
              >
                {intentionalLivingData.aggregate.wow_intentional_living !== 0 && (
                  intentionalLivingData.aggregate.wow_intentional_living > 0 ? (
                    <TrendingUp className="h-3 w-3 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 mr-1" />
                  )
                )}
                {intentionalLivingData.aggregate.wow_intentional_living}%
              </Badge>
            )}
          </div>

          <div className="w-2/3 h-32">
            {hasIntentionalLivingData ? (
              <ChartContainer 
                config={intentionalLivingChartConfig} 
                className="h-full w-full [&_.recharts-cartesian-axis-tick_text]:fill-primary-foreground"
              >
                <AreaChart data={intentionalLivingChartData}>
                <defs>
                  <linearGradient id="fillIntentional" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor="hsl(0, 0%, 100%)"
                      stopOpacity={0.6}
                    />
                    <stop
                      offset="95%"
                      stopColor="hsl(0, 0%, 100%)"
                      stopOpacity={0.2}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="2 2" className="opacity-30" />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  className="text-xs"
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      formatter={(value) => [`${value}%`]}
                    />
                  }
                />
                <Area
                  dataKey="value"
                  type="natural"
                  fill="url(#fillIntentional)"
                  stroke="hsl(0, 0%, 100%)"
                  strokeWidth={5}
                  isAnimationActive={true}
                  animationDuration={1500}
                  animationEasing="ease-in-out"
                />
              </AreaChart>
            </ChartContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <span className="text-primary-foreground text-sm">No data available</span>
              </div>
            )}
          </div>
        </div>

        {/* Time Allocation Details */}
        <Accordion type="single" collapsible defaultValue="more-details" className="w-full">
          <AccordionItem value="more-details" className="border-none">
            <AccordionTrigger className="text-primary-foreground [&>svg]:text-primary-foreground px-4">Time allocation details</AccordionTrigger>
            <AccordionContent className="bg-card rounded-lg p-2">
              {/* Controls row: Calendar filter + View by dropdown */}
              <div className="flex items-center justify-between gap-3 mb-4 px-2">
                {calendarDisplayList.length > 0 && (
                  <CalendarFilter
                    calendars={calendarDisplayList}
                    selectedIds={selectedCalendarIds}
                    onToggle={handleCalendarToggle}
                  />
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">View by</span>
                  <Select value={viewBy} onValueChange={(v) => setViewBy(v as ViewByDimension)}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="life_domain">Life domain</SelectItem>
                      <SelectItem value="label">Labels</SelectItem>
                      <SelectItem value="priority">Priority</SelectItem>
                      <SelectItem value="type">Type</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DoughnutChartContent
                chartData={chartData}
                chartConfig={chartConfig}
                hoveredId={hoveredId}
                excludedSeries={excludedSeries}
                onHover={(id) => setHoveredId(id)}
                onLeave={() => setHoveredId(null)}
                onToggleSeries={handleToggleSeries}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  )
}
